import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Upload, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { staffService } from "@/services/staffService";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface ImportStaffModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const ImportStaffModal: React.FC<ImportStaffModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [file, setFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [results, setResults] = useState<any>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleImport = async () => {
        if (!file) return;

        setIsImporting(true);
        setResults(null);
        try {
            const res = await staffService.importStaff(file);
            setResults(res);
            if (res.failed === 0) {
                toast.success(t("Staff imported successfully"));
                onSuccess();
                onClose();
            } else {
                toast.warning(t("Import completed with some errors"));
            }
        } catch (error) {
            console.error("Import failed", error);
            toast.error(t("Failed to import staff"));
        } finally {
            setIsImporting(false);
        }
    };

    const downloadTemplate = () => {
        const headers = "Email,First Name,Last Name,Phone,Address,Language,Position,Teams\n";
        const example = "john.doe@example.com,John,Doe,+123456789,123 Main St,en,Waiter,Restaurant,Pool\n";
        const blob = new Blob([headers + example], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "staff_import_template.csv";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t("Import Staff")}</DialogTitle>
                    <DialogDescription>
                        {t("Upload a CSV file to import multiple staff members at once.")}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="csv-file">{t("CSV File")}</Label>
                        <Input
                            id="csv-file"
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            disabled={isImporting}
                        />
                    </div>

                    <Button
                        variant="outline"
                        size="sm"
                        className="w-fit gap-2"
                        onClick={downloadTemplate}
                    >
                        <Download className="h-4 w-4" />
                        {t("Download Template")}
                    </Button>

                    {results && (
                        <div className="mt-4 p-4 border rounded-lg bg-muted/50 space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                {t("Imported")}: {results.imported}
                            </div>
                            {results.failed > 0 && (
                                <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    {t("Failed")}: {results.failed}
                                </div>
                            )}
                            {results.errors.length > 0 && (
                                <div className="mt-2 max-h-[150px] overflow-y-auto text-xs space-y-1">
                                    {results.errors.map((err: any, i: number) => (
                                        <div key={i} className="text-destructive">
                                            Row {i + 1}: {err.error} ({err.row.Email || err.row.email})
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={isImporting}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={handleImport}
                        disabled={!file || isImporting}
                        className="gap-2"
                    >
                        {isImporting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Upload className="h-4 w-4" />
                        )}
                        {t("Import")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
